import { useSyncExternalStore } from 'react';

// Everything this browser keeps about who is signed in, and the one place that changes it.
//
// The refresh token is NOT here. It lives in an httpOnly cookie that page script cannot read
// (backend/src/services/authSessionService.js); what localStorage holds is the 15-minute access
// token, which lib/api.ts renews from that cookie as it lapses.
//
// The access token is an external store so the route guards can subscribe to it: when a refresh
// finds the session over and clears it, every ProtectedRoute on screen re-renders and redirects to
// /login on its own — no caller has to remember to.

const TOKEN_KEY = 'token';

/** Display-only marker set while a super admin impersonates someone: the admin's own name. */
export const IMPERSONATOR_NAME_KEY = 'impersonator_userName';

const SESSION_KEYS = [
  TOKEN_KEY,
  'userName',
  'workspaceId',
  'workspacePermissions',
  'role',
  IMPERSONATOR_NAME_KEY,
  // Left behind by the session code that kept the refresh token in localStorage.
  'refreshToken',
  'impersonator_token',
  'impersonator_refreshToken',
  'impersonator_workspaceId',
];

const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

export const getAccessToken = () => localStorage.getItem(TOKEN_KEY);

export function subscribeSession(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Another tab signing in, signing out or refreshing writes the same localStorage; follow it, so
// signing out in one tab signs out all of them. (`key` is null when that tab cleared storage.)
window.addEventListener('storage', (event) => {
  if (event.key === TOKEN_KEY || event.key === null) emit();
});

/** The current access token, re-rendering whenever it changes, in this tab or another. */
export const useAccessToken = () => useSyncExternalStore(subscribeSession, getAccessToken);

/** A renewed access token for the session already in progress. */
export function setAccessToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
  emit();
}

interface NewSession {
  token: string;
  userName: string;
  workspaceId?: string | null;
  /** Set when a super admin starts impersonating — their own name, for the banner. */
  impersonatorName?: string;
}

/** Start a session — sign-in, sign-up, or switching account — with nothing kept from the last one. */
export function setSession({ token, userName, workspaceId, impersonatorName }: NewSession) {
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  localStorage.setItem(TOKEN_KEY, token);
  localStorage.setItem('userName', userName);
  if (workspaceId) localStorage.setItem('workspaceId', workspaceId);
  if (impersonatorName) localStorage.setItem(IMPERSONATOR_NAME_KEY, impersonatorName);
  emit();
}

/** Forget the session in this browser. Server-side sign-out is logout() in lib/api.ts. */
export function clearSession() {
  // Only an actual sign-out is news. A visitor who was never signed in reaching here (a 401, then a
  // refresh with no cookie) must not set the guards and the cache-clear in App.tsx off.
  const hadSession = getAccessToken() !== null;
  SESSION_KEYS.forEach((key) => localStorage.removeItem(key));
  if (hadSession) emit();
}

/** When the access token stops being accepted, in epoch ms; null if it cannot be read. */
function tokenExpiresAt(token: string): number | null {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const { exp } = JSON.parse(atob(payload));
    return typeof exp === 'number' ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * True when the token is past (or within `skewMs` of) its expiry, so a request made with it would
 * only come back 401. A token we cannot read counts as live: the server is the one to judge it.
 */
export function isTokenExpired(token: string, skewMs = 30 * 1000) {
  const expiresAt = tokenExpiresAt(token);
  return expiresAt !== null && expiresAt - skewMs <= Date.now();
}
